import clsx from "clsx";
import { Tooltip } from "@mui/material";

import makeStyles from "@mui/styles/makeStyles";

import { SHORT_DAYS, FULL_DAYS } from "../../consts/strings";
import { ODF_DRIVER_TYPE } from "../../consts/driverTypes";
import { palette } from "../../theme/palette";

import { getTotalDuty } from "../../utils/drivers";

import { DepartIcon, LockIcon, PreciseIcon, ReturnIcon } from "../Icons";
import { TimeInfo } from "../RouteCard/TimeInfo";
import { View } from "../View";

const useStyles = makeStyles({
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    marginTop: "-0.2rem",
  },
  dayContainer: {
    marginLeft: "-3px",
  },
  inline: {
    display: "flex",
    flexDirection: "row",
    marginTop: "4px",
  },
  text: {
    fontSize: "12px",
    color: palette.typography.white.high,
    lineHeight: "16px",
  },
  space: {
    width: "12px",
  },
  flexSpace: {
    flex: 1,
  },

  headerContainer: {
    display: "flex",
    flexDirection: "row",
  },
  headerLeftContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    minWidth: "2rem",
    margin: "-0.1rem -0.3rem -0.1rem -0.3rem",
    padding: "0.1rem 0.3rem 0.1rem 0.3rem",
    borderRadius: "0.5rem 0 0 0.5rem",

    backgroundColor: ({ driverProfileLock, driverDayLock }) => {
      if (driverProfileLock?.changed || driverDayLock?.changed)
        return palette.lockOrange;
      else if (
        (driverProfileLock?.lockedByYou &&
          !driverProfileLock.lockedInDifferentBrowser) ||
        (driverDayLock?.lockedByYou && !driverDayLock.lockedInDifferentBrowser)
      )
        return palette.lockGreen;
      else if (
        driverProfileLock?.lockedByUserCode ||
        driverDayLock?.lockedByUserCode
      )
        return palette.lockRed;
      else return palette.transparent;
    },
  },
  headerRightContainer: {
    margin: "-0.1rem -0.3rem -0.1rem 0",
    padding: "0.1rem 0.3rem 0.1rem 0.2rem",
    borderRadius: "0 0.5rem 0.5rem 0",

    backgroundColor: ({ routeDayLock }) => {
      if (routeDayLock?.changed) return palette.lockOrange;
      else if (
        routeDayLock?.lockedByYou &&
        !routeDayLock.lockedInDifferentBrowser
      )
        return palette.lockGreen;
      else if (routeDayLock?.lockedByUserCode) return palette.lockRed;
      else return palette.transparent;
    },
  },

  lockIcon: {
    color: ({ driverProfileLock, driverDayLock }) => {
      if (
        driverProfileLock?.lockedByUserCode ||
        driverProfileLock?.changed ||
        driverDayLock?.lockedByUserCode ||
        driverDayLock?.changed
      )
        return palette.typography.black.solid;
      else return palette.transparent;
    },
    width: "10px",
    height: "12px",
  },
  tooltip: {
    backgroundColor: "red",

    width: "300px",
    height: "28px",
    "& .MuiTooltip-tooltip": {
      backgroundColor: "red",
      textAlign: "center",
      fontSize: "14px",
      fontWeight: 400,
    },
  },
});
export const DriverCardScheduleDayRow = ({
  dayOfWeek,
  driver,
  driverProfileLocks,
  matrixDays,
}) => {
  const driverDay = matrixDays[dayOfWeek].drivers[driver.driverCode];
  const driverProfileLock = driverProfileLocks[driver.driverCode];
  const driverDayLock = matrixDays[dayOfWeek].driverDayLocks[driver.driverCode];

  const routeCode =
    driverDay.driverType === ODF_DRIVER_TYPE && driverDay.plannedDriverCode
      ? matrixDays[dayOfWeek].drivers[driverDay.plannedDriverCode].routeCode
      : driverDay.routeCode;
  const routeDayLock = matrixDays[dayOfWeek].routeDayLocks[routeCode];

  const styles = useStyles({
    routeCode,
    driverProfileLock,
    driverDayLock,
    routeDayLock,
  });

  const getDriverLockTooltipText = () => {
    if (driverDayLock.changed)
      return `Driver ${driver.driverCode.split("*")[1]} ${driver.driverName} ${
        FULL_DAYS[dayOfWeek]
      } has been changed.`;
    else if (driverDayLock.lockedInDifferentBrowser)
      return `Driver ${driver.driverCode.split("*")[1]} ${driver.driverName} ${
        FULL_DAYS[dayOfWeek]
      } is locked by you in different browser/application.`;
    else if (driverDayLock.lockedByYou)
      return `Driver ${driver.driverCode.split("*")[1]} ${driver.driverName} ${
        FULL_DAYS[dayOfWeek]
      } is locked by you.`;
    else if (driverDayLock.lockedByUserCode)
      return `Driver ${driver.driverCode.split("*")[1]} ${driver.driverName} ${
        FULL_DAYS[dayOfWeek]
      } is locked by ${driverDayLock.lockedByUserCode} ${
        driverDayLock.lockedByUserName
      }.`;
    else return ``;
  };
  const getRouteLockTooltipText = () => {
    if (!routeDayLock) return ``;
    else if (routeDayLock.changed)
      return `Route ${routeCode} ${FULL_DAYS[dayOfWeek]} has been changed.`;
    else if (routeDayLock.lockedInDifferentBrowser)
      return `Route ${routeCode} ${FULL_DAYS[dayOfWeek]} is locked by you in different browser/application.`;
    else if (routeDayLock.lockedByYou)
      return `Route ${routeCode} ${FULL_DAYS[dayOfWeek]} is locked by you.`;
    else if (routeDayLock.lockedByUserCode)
      return `Route ${routeCode} ${FULL_DAYS[dayOfWeek]} is locked by ${routeDayLock.lockedByUserCode} ${routeDayLock.lockedByUserName}.`;
    else return ``;
  };

  return (
    <View className={clsx(styles.dayContainer, styles.text, styles.inline)}>
      <View className={styles.headerContainer}>
        <Tooltip
          title={getDriverLockTooltipText()}
          enterDelay={500}
          enterNextDelay={500}
        >
          <View className={styles.headerLeftContainer}>
            <LockIcon className={styles.lockIcon} />
            <View className={styles.flexSpace} />
            {`${SHORT_DAYS[dayOfWeek]}: `}
          </View>
        </Tooltip>
        <Tooltip
          title={getRouteLockTooltipText()}
          enterDelay={500}
          enterNextDelay={500}
        >
          <View className={styles.headerRightContainer}>
            {routeCode || "------"}
          </View>
        </Tooltip>
      </View>
      <View className={styles.space} />
      <TimeInfo time={[driverDay.dutyStartTime, driverDay.dutyEndTime]} />
      <View className={styles.space} />
      <TimeInfo time={driverDay.plannedDepartTime} icon={<DepartIcon />} />
      <View className={styles.space} />
      <TimeInfo time={getTotalDuty(driverDay)} icon={<PreciseIcon />} />
      <View className={styles.space} />
      <TimeInfo time={driverDay.plannedReturnTime} icon={<ReturnIcon />} />
    </View>
  );
};

export const DriverCardScheduleContent = ({
  driver,
  driverProfileLocks,
  matrixDays,
}) => {
  const styles = useStyles();

  return (
    <View className={styles.container}>
      {[1, 2, 3, 4, 5, 6, 0].map((dayOfWeek) => (
        <View key={dayOfWeek}>
          <DriverCardScheduleDayRow
            dayOfWeek={dayOfWeek}
            driver={driver}
            driverProfileLocks={driverProfileLocks}
            matrixDays={matrixDays}
          />
        </View>
      ))}
    </View>
  );
};
